<template>
  <WdsStack gap-sm direction-column class="tw-w-full filter-full-width">
    <WdsSelect
      v-model="internalSelection"
      multi-select
      searchable
      :label="meta.filter.quickFilter ? '' : $t('Engagement type')"
      :list="engagementTypeSetupOptions"
      :placeholder="$t('All engagement types')"
      class="tw-flex-1"
      can-deselect
      labels-primary
      :disabled="loading"
    >
      <template #option="{ option }">
        <div v-if="option.isHeader" class="header-option tw-w-full" @mousedown.stop>
          <WdsText v-if="option.label" secondary>
            {{ option.label }}
          </WdsText>
        </div>
        <span v-else class="single-line" :title="option.label">
          {{ option.label }}
        </span>
      </template>
    </WdsSelect>
  </WdsStack>
</template>

<script setup lang="ts">
import { WdsSelect, WdsStack, WdsText } from '@worksome/wds-components'
import { computed, ComputedRef, ModelRef } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { $t } from '@/composables/translation'
import { EngagementTypeSetup } from '@/gql/graphql'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'
import { uuid } from '@/utils/tools'
import { graphql } from '@/gql'

defineProps<{ meta: FilterMetaDataProp }>()

const selectedEngagementTypeSetups: ModelRef<Array<{ value: string }>> = defineModel({
  default: () => [],
})

// Query to fetch used engagement type setups for the selected company
const { result: companyResult, loading } = useQuery(
  graphql(`
    query CompanyUsedEngagementTypeSetups($id: ID!) {
      company(id: $id) {
        id
        usedEngagementTypeSetups
      }
    }
  `),
  () => ({
    id: currentAccount.globalId,
  }),
  {
    clientId: 'publicApi',
  },
)

const usedEngagementTypeSetups: ComputedRef<EngagementTypeSetup[]> = computed(
  () => companyResult.value?.company?.usedEngagementTypeSetups || [],
)

// Define which UI options map to which API values.
// This is used to prevent showing duplicate options for similar engagement type setups (e.g. W2 and W2Classify).
const uiToApiMapping: Record<string, string[]> = {
  [EngagementTypeSetup.Ic1099]: [EngagementTypeSetup.Ic1099, EngagementTypeSetup.Ic1099Classify],
  [EngagementTypeSetup.W2]: [EngagementTypeSetup.W2, EngagementTypeSetup.W2Classify],
  [EngagementTypeSetup.Paye]: [EngagementTypeSetup.Paye, EngagementTypeSetup.PayeClassify],
  [EngagementTypeSetup.InsideIr35]: [
    EngagementTypeSetup.InsideIr35,
    EngagementTypeSetup.InsideIr35Classify,
  ],
  [EngagementTypeSetup.GlobalPayroll]: [
    EngagementTypeSetup.GlobalPayroll,
    EngagementTypeSetup.GlobalEmployeeClassify,
  ],
  [EngagementTypeSetup.OutsideIr35]: [
    EngagementTypeSetup.OutsideIr35,
    EngagementTypeSetup.OutsideIr35Classify,
  ],
  [EngagementTypeSetup.SoleTrader]: [
    EngagementTypeSetup.SoleTrader,
    EngagementTypeSetup.SoleTraderClassify,
  ],
  [EngagementTypeSetup.GlobalContractorClassify]: [EngagementTypeSetup.GlobalContractorClassify],
  [EngagementTypeSetup.StatementOfWork]: [EngagementTypeSetup.StatementOfWork],
  [EngagementTypeSetup.Default]: [EngagementTypeSetup.Default],
  [EngagementTypeSetup.Umbrella]: [EngagementTypeSetup.Umbrella],
  [EngagementTypeSetup.StaffingAgency]: [EngagementTypeSetup.StaffingAgency],
  [EngagementTypeSetup.Pending]: [EngagementTypeSetup.Pending],
}

// Engagement type setup labels for filter options
const uiLabelMapping: Record<string, string> = {
  [EngagementTypeSetup.Ic1099]: $t('1099'),
  [EngagementTypeSetup.W2]: $t('W-2'),
  [EngagementTypeSetup.Paye]: $t('PAYE'),
  [EngagementTypeSetup.InsideIr35]: $t('Inside IR35'),
  [EngagementTypeSetup.GlobalPayroll]: $t('Global Payroll'),
  [EngagementTypeSetup.OutsideIr35]: $t('Outside IR35'),
  [EngagementTypeSetup.SoleTrader]: $t('Sole Trader'),
  [EngagementTypeSetup.GlobalContractorClassify]: $t('Global Contractor'),
  [EngagementTypeSetup.StatementOfWork]: $t('Statement of Work (SoW)'),
  [EngagementTypeSetup.Default]: $t('Default'),
  [EngagementTypeSetup.Umbrella]: $t('Umbrella'),
  [EngagementTypeSetup.StaffingAgency]: $t('Staffing Agency'),
  [EngagementTypeSetup.Pending]: $t('Pending'),
}

// Create reverse mapping
const apiToUiMapping: Record<string, string> = {}
Object.entries(uiToApiMapping).forEach(([uiValue, apiValues]) => {
  apiValues.forEach((apiValue) => {
    apiToUiMapping[apiValue] = uiValue
  })
})

// Wrapper that handles transformation between UI and API values
const internalSelection = computed({
  get() {
    const uiValues = new Set<string>()
    selectedEngagementTypeSetups.value.forEach((item) => {
      const apiValue = item.value
      const uiValue = apiToUiMapping[apiValue]
      if (uiValue) {
        uiValues.add(uiValue)
      } else {
        uiValues.add(apiValue)
      }
    })

    return Array.from(uiValues).map((value) => ({
      label: uiLabelMapping[value] || $t(value),
      value,
    }))
  },
  set(uiValues: Array<{ label: string; value: string }>) {
    const apiValues = new Set<string>()

    uiValues.forEach((item) => {
      const uiValue = item.value
      const mapped = uiToApiMapping[uiValue]

      if (mapped) {
        mapped.forEach((apiValue) => apiValues.add(apiValue))
      } else {
        apiValues.add(uiValue)
      }
    })

    selectedEngagementTypeSetups.value = Array.from(apiValues).map((v) => ({ value: v }))
  },
})

// Group engagement types by category
const payrollSetups = [
  EngagementTypeSetup.W2,
  EngagementTypeSetup.Paye,
  EngagementTypeSetup.InsideIr35,
  EngagementTypeSetup.GlobalPayroll,
]

const contractorSetups = [
  EngagementTypeSetup.Ic1099,
  EngagementTypeSetup.OutsideIr35,
  EngagementTypeSetup.SoleTrader,
  EngagementTypeSetup.GlobalContractorClassify,
  EngagementTypeSetup.StatementOfWork,
  EngagementTypeSetup.Default,
]

const externalSetups = [EngagementTypeSetup.Umbrella, EngagementTypeSetup.StaffingAgency]

const pendingSetups = [EngagementTypeSetup.Pending]

// Define the options shown to the user with grouped headers
const engagementTypeSetupOptions = computed(() => {
  const options: Array<{
    label: string
    value: string
    isHeader?: boolean
    id?: string
  }> = []

  // Helper to check if a setup should be included based on company's used setups
  const shouldIncludeSetup = (setup: string): boolean => {
    // Check if any of the API values for this UI setup are in the used setups
    const apiValues = uiToApiMapping[setup] || []
    return apiValues.some((apiValue) =>
      usedEngagementTypeSetups.value.includes(apiValue as EngagementTypeSetup),
    )
  }

  // Helper to add options for a category
  const addCategory = (headerLabel: string, setups: string[]) => {
    const categoryOptions = setups
      .filter((setup) => uiToApiMapping[setup] && shouldIncludeSetup(setup))
      .map((setup) => ({
        label: uiLabelMapping[setup] || $t(setup),
        value: setup,
      }))

    if (categoryOptions.length > 0) {
      options.push({
        label: headerLabel,
        value: `header-${uuid()}`,
        isHeader: true,
        id: uuid(),
      })
      options.push(...categoryOptions)
    }
  }

  addCategory($t('Payroll'), payrollSetups)
  addCategory($t('Contractor'), contractorSetups)
  addCategory($t('External'), externalSetups)
  addCategory('', pendingSetups) // Remove title since it's a duplicate name

  return options
})
</script>

<style lang="scss" scoped>
:deep(.wds-select-list) {
  .wds-select-option {
    &:has(.header-option) {
      @apply tw-m-0 tw-p-0 tw-rounded-none tw-border-t tw-border-[#f0f0f0];
      &:hover {
        @apply tw-bg-white;
      }
    }

    &:nth-of-type(2) {
      @apply tw-border-t-0;

      .header-option {
        @apply tw-pt-1;
      }
    }
  }
}

.header-option {
  @apply tw-pt-3 tw-pb-2 tw-px-4 tw-cursor-auto;

  // Empty header (just a divider)
  &:empty,
  &:has(.wds-text:empty) {
    @apply tw-pb-0;
  }
}

.single-line {
  @apply tw-break-keep tw-text-ellipsis tw-whitespace-nowrap tw-overflow-hidden tw-max-w-full;
}
</style>
